import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useTranslation } from "react-i18next";

import {
  AdvancedSearchConfig,
  AdvancedSearchFieldConfig,
  findFieldByKey,
  filterForSortFields,
  filterOutForeignFields,
  getFieldLabel,
} from "@/config/forms/advanced-search/advanced-search-fields-def";
import { ChevronsUpDownIcon } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { CheckIcon } from "@radix-ui/react-icons";
import { useCallback, useMemo } from "react";

export const AdvancedQueryFieldSelector = ({
  value,
  className,
  onSelect,
  config,
  hiddenFieldKeys,
  allowForeign,
  forSort,
}: {
  value?: AdvancedSearchFieldConfig;
  onSelect: (value: AdvancedSearchFieldConfig) => void;
  config: AdvancedSearchConfig;
  /** Field keys masked in the fiche → hidden from the dropdown. */
  hiddenFieldKeys?: ReadonlySet<string>;
  className?: string;
  allowForeign?: boolean; // allow a field that reference a foreign table
  forSort?: boolean;
}) => {
  const { t } = useTranslation();

  const cleanConfig = useMemo(() => {
    if (allowForeign !== false) return config;
    return forSort ? filterForSortFields(config) : filterOutForeignFields(config);
  }, [config, allowForeign, forSort]);

  const selectedField = useMemo(
    () => (value?.key ? findFieldByKey(value.key, cleanConfig) : undefined),
    [value?.key, cleanConfig],
  );

  const renderCommandItem = useCallback(
    (field: AdvancedSearchFieldConfig) => {
      const translatedLabel = t(getFieldLabel(field, forSort));

      return (
        <CommandItem
          key={field.key}
          value={`${field.key} ${translatedLabel}`}
          className={cn("pl-4")}
          onSelect={() => {
            const relatedConfig = findFieldByKey(field.key, cleanConfig);

            if (!relatedConfig) return;

            onSelect(relatedConfig);
          }}
        >
          {translatedLabel}
          <CheckIcon
            className={cn(
              "ml-auto",
              field.key === value?.key ? "opacity-100" : "opacity-0",
            )}
          />
        </CommandItem>
      );
    },
    [cleanConfig, onSelect, value?.key, t, forSort],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          className={cn(
            "w-1/2 min-h-fit justify-between !whitespace-wrap text-left",
            className,
          )}
        >
          {value != undefined && value.key
            ? t(getFieldLabel(selectedField ?? value, forSort))
            : t("selectField")}
          <ChevronsUpDownIcon className="opacity-50 h-3 mr-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-fit p-0">
        <Command
          filter={(filterValue, search) =>
            filterValue
              ?.toLocaleLowerCase()
              ?.includes(search.toLocaleLowerCase())
              ? 1
              : 0
          }
        >
          <CommandInput placeholder={t("selectField")} className="h-9" />
          <CommandList>
            <CommandEmpty>{t("rechercheVideRechercheAvancee")}</CommandEmpty>
            {cleanConfig.map((o) => {
              if (o.type === "group") {
                // Drop fields masked in the fiche; hide the group if empty.
                const visibleFields = o.fields.filter(
                  (ofield) => !hiddenFieldKeys?.has(ofield.key),
                );
                if (visibleFields.length === 0) return null;
                return (
                  <CommandGroup
                    key={o.label}
                    heading={t(o.label)}
                    className="[&_[cmdk-group-heading]]:text-primary [&_[cmdk-group-heading]]:font-semibold"
                  >
                    {visibleFields.map((ofield) => renderCommandItem(ofield))}
                  </CommandGroup>
                );
              }
              if (hiddenFieldKeys?.has(o.key)) return null;
              return renderCommandItem(o);
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
